import React, { useCallback, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useParams } from 'react-router-dom';
import styles from '@/pages/decedent-details/styles.less';
import { Decedent, PrimaryUser } from '@/pages/decedent-details/components/index';
import { routes } from '@/app/routing/index';
import { EditDecedentMainInfo, EditPrimaryUser } from '@/common/containers/Modals/index';
import { useToggle } from '@/common/hooks/index';
import {
  useDecedentQuery,
  useEditDecedentMutation,
  useEditUserPersonaMutation,
} from '@/entities/decedent';

const DecedentDetails = () => {
  const [decedentEdit, toggleDecedentEdit] = useToggle(false);
  const [primaryEdit, togglePrimaryEdit] = useToggle(false);
  const { id } = useParams();
  const { data } = useDecedentQuery(id);
  const editDecedentMutation = useEditDecedentMutation();
  const editUserPersonaMutation = useEditUserPersonaMutation();
  const [profileOverride, setProfileOverride] = useState({});
  const profile = useMemo(
    () => ({
      decedent: profileOverride.decedent || data?.decedent,
      primaryUser: profileOverride.primaryUser || data?.primaryUser,
    }),
    [data?.decedent, data?.primaryUser, profileOverride.decedent, profileOverride.primaryUser]
  );

  const onEditDecedent = useCallback(
    async ({ form, id }) => {
      try {
        const updated = await editDecedentMutation.mutateAsync({ form, id });
        setProfileOverride((prev) => ({ ...prev, decedent: updated }));
      } catch (e) {
        console.error(e);
      }
    },
    [editDecedentMutation]
  );

  const onPrimaryEdit = useCallback(
    async (form) => {
      try {
        const updated = await editUserPersonaMutation.mutateAsync(form);
        setProfileOverride((prev) => ({ ...prev, primaryUser: updated }));
      } catch (e) {
        console.error(e);
      }
    },
    [editUserPersonaMutation]
  );

  return (
    <>
      {profile.decedent && (
        <EditDecedentMainInfo
          decedent={profile.decedent}
          onSubmit={onEditDecedent}
          open={decedentEdit}
          onClose={toggleDecedentEdit}
        />
      )}
      {profile.primaryUser && (
        <EditPrimaryUser
          user={profile.primaryUser}
          onSubmit={onPrimaryEdit}
          open={primaryEdit}
          onClose={togglePrimaryEdit}
        />
      )}
      <section className={styles.page}>
        <Link className={styles.linkBack} to={routes.adminPanel.decedents}>
          <ArrowBackIcon /> Back
        </Link>
        <div className={styles.header}>
          {profile.decedent &&
            (() => {
              const { firstName, lastName, middleName } = profile.decedent;
              return <h3>{`${firstName} ${middleName} ${lastName}`}</h3>;
            })()}
        </div>
        <div className={styles.content}>
          <Grid container spacing={2}>
            <Grid size={7}>
              <Decedent
                profile={profile.decedent}
                openEdit={toggleDecedentEdit}
              />
            </Grid>
            <Grid size={5}>
              <PrimaryUser
                user={profile.primaryUser}
                openEdit={togglePrimaryEdit}
              />
            </Grid>
          </Grid>
        </div>
      </section>
    </>
  );
};

export default DecedentDetails;
